import {
  GetAvailableFiltersParams,
  InteractionGetAvailableTagsReq,
  InteractionGetAvailableTagsRes,
  QueryType,
  ReportType,
  TimeRangeConfig,
} from '@/api/generated/model';
import { COHORT_USER_FILTER_SELECTABLE_COLUMNS } from '@/domains/live-activity/cohorts/components/rule-builder/cohort-user-filters';
import { QUERY_KEY_AVAILABLE_FILTERS, QUERY_KEY_AVAILABLE_TAGS } from '@/constants/query-keys';
import { getNebulyPageTimeRangeDefault } from '@/domains/pages/utils/nebuly-page-time-range';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useEnvironmentContext } from '@/providers/environment.provider';
import { FILTERS } from '@/models/common/filters';
import { AggregateBy } from '@/domains/dashboard/components/GlobalfiltersRow';
import { getAvailableTags } from '@/api/generated/interactions/interactions';
import { useGetAvailableFilters } from '@/api/generated/projects/projects';
import { getDateTRangeFromKind } from '@/utils/get-date-range-from-kind';
import { getTimezoneName } from '@/utils/date.util';

type AvailableTagsQueryOptions = {
  staleTime?: number;
  cacheTime?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean;
};

export const useAvailableTags = (
  projectId: string | undefined,
  filters: InteractionGetAvailableTagsReq['filters'] = [],
  timeRange?: TimeRangeConfig,
  queryOptions?: AvailableTagsQueryOptions,
): { availableTags: InteractionGetAvailableTagsRes | undefined; isLoading: boolean } => {
  const resolvedTimeRange = useMemo(() => {
    const base = timeRange ?? getNebulyPageTimeRangeDefault();
    if (base.start && base.end) return base;

    return getDateTRangeFromKind({
      kind: base.kind,
      start: base.start ?? '',
      end: base.end ?? '',
      timezone: base.timezone ?? getTimezoneName(),
      recompute: base.recompute ?? true,
    });
  }, [timeRange]);

  const tagsPayload = useMemo(
    () =>
      ({
        filters,
        project_id: projectId!,
        time_range: resolvedTimeRange,
      }) as InteractionGetAvailableTagsReq,
    [filters, resolvedTimeRange, projectId],
  );
  const { data: availableTags, isLoading } = useQuery({
    queryKey: [QUERY_KEY_AVAILABLE_TAGS, tagsPayload],
    queryFn: ({ signal }) => getAvailableTags(tagsPayload, { signal }),
    enabled: !!projectId,
    ...queryOptions,
  });

  return { availableTags, isLoading };
};

type FetchAvailableFiltersParams = {
  projectId: string | undefined;
  filters?: InteractionGetAvailableTagsReq['filters'];
  timeRange?: TimeRangeConfig;
  additionalFilterKinds?: string[];
  type: QueryType;
  reportType?: ReportType | null;
  usage?: 'filter' | 'group_by';
  enabled?: boolean;
  /** Retained for callers; get-available-filters API does not accept this field. */
  aggregateBy?: AggregateBy;
};

export const useFetchAvailableFilters = ({
  projectId,
  type,
  reportType,
  usage,
  enabled = true,
}: FetchAvailableFiltersParams) => {
  const { enableCohorts } = useEnvironmentContext();
  const filtersPayload = useMemo(
    () =>
      ({
        project_id: projectId!,
        query_type: type,
        ...(reportType ? { report_type: reportType } : {}),
        ...(usage ? { usage } : {}),
      }) satisfies GetAvailableFiltersParams,
    [projectId, reportType, type, usage],
  );
  const { data: availableFilters, isLoading } = useGetAvailableFilters(filtersPayload, {
    query: {
      queryKey: [QUERY_KEY_AVAILABLE_FILTERS, projectId, filtersPayload],
      enabled: enabled && !!projectId,
    },
  });

  const filteredData = useMemo(() => {
    if (!availableFilters?.filters) return [];

    // Filter out cohort-related filters when enableCohorts is false
    const baseFilters = enableCohorts
      ? availableFilters.filters
      : availableFilters.filters.filter(
          (filter) => filter !== 'cohort' && filter !== 'cohorts' && !filter.toLowerCase().includes('cohort'),
        );

    // Keep labels available in the picker groups even if backend available-filters lags behind.
    const extra =
      type === 'classification_rule'
        ? enableCohorts
          ? [FILTERS.columnCohort]
          : []
        : type === QueryType.users_table
          ? [...COHORT_USER_FILTER_SELECTABLE_COLUMNS, FILTERS.kindInteractionLabel, FILTERS.kindConversationLabel]
          : [FILTERS.kindInteractionLabel, FILTERS.kindConversationLabel];

    const merged = Array.from(new Set([...baseFilters, ...extra]));

    if (type === QueryType.users_table) {
      return merged;
    }

    return merged.filter((filter) => filter !== FILTERS.lastInteractionTag);
  }, [availableFilters?.filters, enableCohorts, type]);

  return { data: filteredData, items: availableFilters?.items ?? {}, isLoading };
};
